import Fab from '@mui/material/Fab';
import React, { useMemo } from 'react';

import {
  CustomToolbar,
  CustomToolbarAction,
  CustomToolbarMappedAction,
} from '../ToolbarTypes';

import { mapCustomActionsToComponent } from './mapCustomActionsToComponent';

interface UseCustomActionsOptions {
  skipMode?: boolean;
  selectedRowsIdsList?: string[] | number[];
  custom?: CustomToolbar;
}
const ARRAY_CONST: string[] | number[] = [];

export const useToolbarCustomActions = (opt: UseCustomActionsOptions) => {
  const { selectedRowsIdsList = ARRAY_CONST, custom, skipMode = false } = opt;
  return useMemo(() => {
    const mapCustomActions = (
      x: CustomToolbarAction
    ): CustomToolbarMappedAction => {
      const handleProps = x.getHref
        ? {
            href:
              x.mode === 'Delete'
                ? x.getHref(selectedRowsIdsList)
                : x.getHref(selectedRowsIdsList[0]),
          }
        : {
            onClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) =>
              x.mode === 'Delete'
                ? x.handler(selectedRowsIdsList, e)
                : x.handler(selectedRowsIdsList[0], e),
          };
      return { ...x, ...handleProps };
    };
    const isExactlyOneSelected = selectedRowsIdsList.length === 1;
    const isSomethingSelected = selectedRowsIdsList.length > 0;
    const filterEditModeActions = (x: CustomToolbarAction): boolean => {
      return (
        skipMode ||
        x.mode === 'Add' ||
        (x.mode === 'Edit' && isExactlyOneSelected) ||
        (x.mode === 'Delete' && isSomethingSelected)
      );
    };

    return {
      hideDivider: custom?.hideDivider,
      left: custom?.left?.filter(filterEditModeActions).map(mapCustomActions),
      right: custom?.right?.filter(filterEditModeActions).map(mapCustomActions),
    };
  }, [custom, selectedRowsIdsList, skipMode]);
};

export const useToolbarCustomActionsComponents = (
  opt: UseCustomActionsOptions
) => {
  const actions = useToolbarCustomActions(opt);

  return useMemo(() => {
    return {
      left: mapCustomActionsToComponent(
        actions.left?.map((x) => ({ ...x, tooltip: x.title })) ?? []
      ),
      right: mapCustomActionsToComponent(
        actions.right?.map((x) => ({ ...x, tooltip: x.title })) ?? []
      ),
    };
  }, [actions]);
};

export const useMobileToolbarCustomActionsComponents = (
  opt: UseCustomActionsOptions
) => {
  const actions = useToolbarCustomActions(opt);

  return useMemo(() => {
    const all = [...(actions.left ?? []), ...(actions.right ?? [])];

    return (
      <React.Fragment>
        {all.map((x) => (
          <Fab
            ref={x.rootRef}
            key={x.actionKey}
            size="medium"
            color="secondary"
            onClick={x.onClick}
          >
            {<x.IconComponent />}
          </Fab>
        ))}
      </React.Fragment>
    );
  }, [actions]);
};
